// For reference: https://incomeapp.mhc.asia/static/BankAccountInformationGuideforGIROPayment.pdf

import { BANK_DATA, BANK_NAME } from './index';

class BankUtility{

    /**
     * Return bank data or null if bank not registered. 
     * Throw an error if args is not number
     * @param {number} bankCode 
     * @param {number} accountNo 
     * @return {object} return bank data or null if bank not registered.
     */
    getBankDetails(bankCode, accountNo){
        if(isNaN(bankCode)) throw new Error("Bank code is missing");
        if(isNaN(accountNo)) throw new Error("Bank account no is missing");

        // Handle special case for DBS and POSBank since it have sama bank code
        if(bankCode === "7171" ) {
            if(accountNo.length === 9)
                return BANK_DATA.find( data => data.BANK_NAME == BANK_NAME.POSBank);
            else
                return BANK_DATA.find( data => data.BANK_NAME == BANK_NAME.DBS);
        }

        // Handle special case for UOB and UOB (Virtual Account Number)
        if(bankCode === "7375"){
            if(accountNo.length === 10)
                return BANK_DATA.find( data => data.ALIAS == "UOB");
            else 
                return BANK_DATA.find( data => data.ALIAS == "UOB (Virtual Account Number)");
        }

        // Handle other banks
        for(let data of BANK_DATA){
            if(data.BANK_CODE === bankCode) return data;
        }
        return null;
    }

    /**
     * Return bank name, or null if bank not registered. 
     * Throw an error if arg is not number
     * @param {number} bankCode 
     * @param {number} accountNo 
     * @return {string} return bank name, or null if bank not registered.
     */
    getBankName(bankCode, accountNo){
        if(isNaN(bankCode)) throw new Error("Bank code is missing");
        if(isNaN(accountNo)) throw new Error("Bank account no is missing");

        // Get bank details
        let data = this.getBankDetails(bankCode, accountNo);
        if(!data) return null;

        return data.BANK_NAME;
    }

    /**
     * Return valid bank account number, or null if bank not registered. 
     * Throw an error if arg is not number
     * @param {number} bankCode 
     * @param {number} accountNo 
     * @return {number} return bank account number, or null if bank not registered.
     */
    getAccountNo(bankCode, accountNo){

        if(isNaN(bankCode)) throw new Error("Bank code is missing");
        if(isNaN(accountNo)) throw new Error("Bank account no is missing");

        // Get bank details
        let data = this.getBankDetails(bankCode, accountNo);
        if(!data) return null;

        // Account no need to splice
        if( data.BANK_NAME === BANK_NAME.OCBC ||
            data.BANK_NAME === BANK_NAME.HSBCCORPORATE ||
            data.BANK_NAME === BANK_NAME.HSBCPERSONAL ||
            data.BANK_NAME === BANK_NAME.SBI
            ){
            return accountNo.substring(3);
        }
        // Account no remain same
        else{
            return accountNo;
        }
    }

    /**
     * Return branch code, or null if bank not registered. 
     * Throw an error if arg is not number
     * @param {number} bankCode 
     * @param {number} accountNo 
     * @return {number} return bank code, or null if bank not registered.
     */
    getBranchCode(bankCode, accountNo){

        // Validate bankCode
        if(isNaN(bankCode)) throw new Error("Bank Code is missing");

        // Validate accountNo
        if(isNaN(accountNo)) throw new Error("Account no is missing");
        if(accountNo.length <3) throw new Error("Invalid account no total digit");;

        // Get bank details
        let data = this.getBankDetails(bankCode, accountNo);
        if(!data) return null;

        // Handle POSBANK or DBS
        if(data.BANK_NAME === BANK_NAME.POSBank || data.BANK_NAME === data.BANK_NAME.DBS){
            return (accountNo.length === 9) ? "081" : accountNo.substring(0,3);
        }
        // Handle STANDARD CHARTERED
        else if(data.BANK_NAME === BANK_NAME.STANDARDCHARTERED){
            return "0"+accountNo.substring(0,2);
        }
        // Handle UOB 
        else if(data.BANK_NAME === BANK_NAME.UOB && data.ALIAS === "UOB"){
            const keyCode = accountNo.substring(0,3);
            const branch = data.BRANCH_LIST.find( branch => branch.KEY_CODE === keyCode);
            if(branch)
                return branch.BRANCH_CODE;
            else
                return null;
        }
        // Handle UOB Virtual Account Number
        else if(data.BANK_NAME === BANK_NAME.UOB && data.ALIAS === "UOB (Virtual Account Number)"){
            return "001";
        }
        // Handle Bank of China
        else if(data.BANK_NAME === BANK_NAME.BANK_OF_CHINA ){
            return null;
        }
        // Handle other banks
        else{
            return accountNo.substring(0,3);
        }

    }

    /**
     * Check if given args is valid bank data, return null if bank not registered.
     * Throw an error if not valid
     * @param {number} bankCode 
     * @param {number} accountNo 
     * @return {boolean} return true, or null if bank not registered.
     */
    isValidAccountNoDigit(bankCode, accountNo){

        if(isNaN(bankCode)) throw new Error("Bank code is missing");
        if(isNaN(accountNo)) throw new Error("Account no is missing");

        // Get bank details
        let data = this.getBankDetails(bankCode, accountNo);
        if(!data) return null;

        // --- Validation for specific bank

        // Validate POS Bank account no digit
        if(data.BANK_NAME === BANK_NAME.POSBank && (accountNo+"").length !== 9){
            throw new Error("POSBank account number must contain 9 digit");
        }

        // Validate DBS account no digit
        if(data.BANK_NAME === BANK_NAME.DBS && (accountNo+"").length !== 10){
            throw new Error("DBS account number must contain 10 digit");
        }

        // Validation Bank of China
        if(data.BANK_NAME === BANK_NAME.BANK_OF_CHINA && (accountNo.startsWith("6500") || !accountNo.startsWith("9") || accountNo.length !== 11) ){
            throw new Error(`Invalid account number for ${data.BANK_NAME}. Please enter account number after '6500'. Must contain 11 digits (beginning with 9) `);
        }
        // --- End of validation for specific bank


        // -- Begin general validation --

        // Validate account no minimum digit
        if(data.ACCOUNT_NO_MIN_DIGIT){
            if(accountNo.length < data.ACCOUNT_NO_MIN_DIGIT) throw new Error("Invalid account no minimum digit")
        }

        // Validate account no max digit
        if(data.ACCOUNT_NO_MAX_DIGIT){
            if(accountNo.length > data.ACCOUNT_NO_MAX_DIGIT) throw new Error("Invalid account no maximum digit")
        }

        // Validate account no exact digit
        if(Array.isArray(data.ACCOUNT_NO_EXACT_DIGIT) && data.ACCOUNT_NO_EXACT_DIGIT.length > 0){
            if(!data.ACCOUNT_NO_EXACT_DIGIT.includes(accountNo.length)) throw new Error("Invalid account no digit")
        }

        // Validate account no forbidden digit
        if(Array.isArray(data.ACCOUNT_NO_FORBIDDEN_DIGIT) && data.ACCOUNT_NO_FORBIDDEN_DIGIT.length > 0){
            if(data.ACCOUNT_NO_FORBIDDEN_DIGIT.includes(accountNo.length)) throw new Error("Invalid account exact digit")
        }

        return true;
    }

    isBankCodeExist(bankList, bankCode){
        if(!Array.isArray(bankList) || !bankCode) return null;
        for (var i = 0; i < bankList.length; i++) {
            if (bankList[i].c === bankCode) {
                return bankList[i].n;
            }
        }

        return null;
    }

    isBranchCodeExist(bankList, bankCode, branchCode){

        if(!Array.isArray(bankList) || !bankCode || !branchCode) return null;
        
        for (let i = 0; i < bankList.length; i++) {
            if (bankList[i].c === bankCode) {
                for(let branch of bankList[i].branches){
                    if(branch.c === branchCode) return branch.c;
                }
            }
        }

        return null;
    }

}

export default BankUtility;